import React, { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';

const UserLimitsTab = forwardRef(function UserLimitsTab({ config, onUpdate, hideWizardSaveBar = false }, ref) {
  const [formData, setFormData] = useState({
    maxClaimsPerUser: 10
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const savedMaxClaims = config?.userLimits?.maxClaimsPerUser ?? 10;
  const isDirty = useMemo(() => {
    if (formData.maxClaimsPerUser === '') return true;
    const n = Number(formData.maxClaimsPerUser);
    if (!Number.isFinite(n)) return true;
    return n !== savedMaxClaims;
  }, [formData.maxClaimsPerUser, savedMaxClaims]);

  useEffect(() => {
    if (config?.userLimits) {
      setFormData(config.userLimits);
    }
  }, [config]);

  const handleSave = async () => {
    // Validate before saving
    if (!formData.maxClaimsPerUser || formData.maxClaimsPerUser < 1) {
      setMessage({ type: 'error', text: 'Maximum claims per user must be at least 1.' });
      return;
    }

    setSaving(true);
    setMessage(null);
    try {
      await onUpdate({ userLimits: formData });
      setMessage({ type: 'success', text: 'User limits saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save changes. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  useImperativeHandle(ref, () => ({
    saveStep: async () => {
      if (!formData.maxClaimsPerUser || formData.maxClaimsPerUser < 1) {
        return { ok: false, error: 'Maximum claims per user must be at least 1.' };
      }
      setSaving(true);
      setMessage(null);
      try {
        await onUpdate({ userLimits: formData });
        return { ok: true };
      } catch {
        return { ok: false, error: 'Failed to save user limits.' };
      } finally {
        setSaving(false);
      }
    }
  }), [formData, onUpdate]);

  return (
    <div className="tab-panel">
      <div className="tab-header">
        <h2>User Limitation Rules</h2>
        <p>Prevent abuse by controlling how many claims each user can submit.</p>
      </div>

      <div className="rules-form">
        <div className="form-section">
          <div className="form-section-title">Claim Limits</div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Maximum Claims per User</label>
              <input
                type="number"
                min="1"
                max="100"
                inputMode="numeric"
                value={formData.maxClaimsPerUser}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    setFormData({ ...formData, maxClaimsPerUser: '' });
                    return;
                  }
                  if (value.includes('-')) return;
                  const parsedValue = parseInt(value, 10);
                  if (!isNaN(parsedValue) && parsedValue >= 0) {
                    setFormData({
                      ...formData,
                      maxClaimsPerUser: parsedValue
                    });
                  }
                }}
              />
              <span className="form-hint">Total number of claims a user can submit during the campaign. Once reached, further submissions will be blocked.</span>
            </div>
          </div>
        </div>

        {message && (
          <div className={`status-message ${message.type}`}>
            {message.type === 'success' ? '✓' : '✕'} {message.text}
          </div>
        )}

        {!hideWizardSaveBar && (
          <div className="save-bar">
            <button 
              className="btn-save" 
              onClick={handleSave}
              disabled={saving || !isDirty}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

export default UserLimitsTab;
